import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core'
import maplibregl from 'maplibre-gl'
import { MapEventsService } from 'src/app/core/services/map-events.service'
import { MapService } from 'src/app/core/services/map.service'
import { SpritePositionService } from 'src/app/core/services/sprite-position.service'
import { APP_CONFIG } from 'src/app/app.config'
import { AppConfigModel } from 'src/app/core/models'
import { MapSignsIcons, MapSupsIcons } from 'src/app/core/enums/map-icons.enum'
import { MapSignsDisplayTypes } from 'src/app/core/enums/sign-model'
import { AuthService } from 'src/app/core/services/auth.service'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { CustomerReportingService } from '../../services/customer-reporting.service'
import { getDefaultSignReportTitle, SignReport } from '../../models/sign-report.model'

export const MAP_REPORT_CATEGORIES = [
  { value: 'sign-missing', label: 'Sign missing' },
  { value: 'other', label: 'Other' },
] as const

@Component({
  selector: 'app-customer-map-report-dialog',
  templateUrl: './customer-map-report-dialog.component.html',
  styleUrls: ['./customer-map-report-dialog.component.scss', '../report-form.scss'],
  standalone: false,
})
export class CustomerMapReportDialogComponent implements OnInit, OnDestroy {
  public signTypes = [
    ...Object.values(MapSignsIcons).filter((v) => !v.includes('UNKNOWN')),
    'OTHER',
  ]
  public signDisplayTypes = Object.values(MapSignsDisplayTypes)
  public supplementarySignTypes = [
    ...Object.values(MapSupsIcons).filter((v) => !v.includes('UNKNOWN')),
    'OTHER',
  ]
  public categories = MAP_REPORT_CATEGORIES

  public report: SignReport = this.emptyReport()
  public readonly reportSubmitted = signal(false)
  public readonly reportSubmitting = signal(false)
  public readonly reportError = signal(false)
  public readonly ticketKey = signal<string | null>(null)
  public readonly ticketUrl = signal<string | null>(null)

  public marker: any = null
  public popup: any = null
  public readonly isMobileFormOpen = signal(false)
  public lastClickLocation: { lng: number; lat: number } | null = null

  private mapRotationListener: (() => void) | null = null
  private boundMapClick: any

  @ViewChild('reportPopupTemplate', { static: true }) popupTemplate!: TemplateRef<any>

  constructor(
    @Inject(APP_CONFIG) private readonly config: AppConfigModel,
    private readonly mapEventsService: MapEventsService,
    private readonly mapService: MapService,
    private readonly spritePositionService: SpritePositionService,
    private readonly customerReportingService: CustomerReportingService,
    private readonly authService: AuthService,
    private readonly appState: AppStateService,
    private readonly viewContainerRef: ViewContainerRef,
  ) {
    this.boundMapClick = (ev: any) => this.handleMapClick(ev)
  }

  ngOnInit() {
    this.mapEventsService.registerMapClick(this.boundMapClick)
    this.mapService.map.getCanvas().style.cursor = 'crosshair'
  }

  handleMapClick(ev: { lngLat: { lng: number; lat: number }; originalEvent: MouseEvent }) {
    this.lastClickLocation = { lng: ev.lngLat.lng, lat: ev.lngLat.lat }
    this.resetForm()
    this.closePopup()

    this.createMarker(ev.lngLat, this.getMarkerIconName(), this.report.bearing ?? 0)

    const isMobile = window.innerWidth < 768
    if (isMobile) {
      const container = this.mapService.map.getContainer()
      const mapHeight = container.clientHeight
      const visibleMapCenterOffset = -(mapHeight / 4)
      this.mapService.map.easeTo({
        center: [ev.lngLat.lng, ev.lngLat.lat],
        offset: [0, visibleMapCenterOffset],
        duration: 300,
      })
      this.isMobileFormOpen.set(true)
      return
    }

    const viewRef = this.viewContainerRef.createEmbeddedView(this.popupTemplate)
    viewRef.detectChanges()
    const popupContent = viewRef.rootNodes[0]

    this.popup = new maplibregl.Popup({ offset: 25, closeButton: false, maxWidth: 'none' })
      .setLngLat([ev.lngLat.lng, ev.lngLat.lat])
      .setDOMContent(popupContent)
      .addTo(this.mapService.map)

    this.popup.on('close', () => {
      this.cleanupMarker()
      this.popup = null
      this.unregisterMapRotationListener()
    })
  }

  closeForm() {
    if (this.popup) {
      this.popup.remove()
    } else {
      this.isMobileFormOpen.set(false)
      this.cleanupMarker()
    }
  }

  onSignTypeChange() {
    if (this.isZoneSign()) {
      this.report.speedLimit = 20
    } else {
      this.report.speedLimit = 50
    }
    this.report.displayType = MapSignsDisplayTypes.ANALOG
    if (this.lastClickLocation) {
      this.createMarker(this.lastClickLocation, this.getMarkerIconName(), this.report.bearing ?? 0)
    }
  }

  onSpeedChange() {
    if (this.lastClickLocation) {
      this.createMarker(this.lastClickLocation, this.getMarkerIconName(), this.report.bearing ?? 0)
    }
  }

  onBearingChange() {
    if (this.lastClickLocation) {
      this.createMarker(this.lastClickLocation, this.getMarkerIconName(), this.report.bearing ?? 0)
    }
  }

  onDisplayTypeChange() {
    if (this.lastClickLocation) {
      this.createMarker(this.lastClickLocation, this.getMarkerIconName(), this.report.bearing ?? 0)
    }
  }

  onSupplementaryChange() {
    if (this.lastClickLocation) {
      this.createMarker(
        this.lastClickLocation,
        this.getMarkerIconName(),
        this.report.bearing ?? 0,
        this.report.supplementarySigns,
      )
    }
  }

  removeSupplementarySign(sup: string) {
    this.report.supplementarySigns = this.report.supplementarySigns.filter((s) => s !== sup)
    this.onSupplementaryChange()
  }

  canBeDigital(): boolean {
    return (
      this.report.signType.startsWith('SPEED') ||
      this.report.signType === MapSignsIcons.ALL_RESTRICTIONS_END
    )
  }

  isZoneSign(): boolean {
    return (
      this.report.signType === MapSignsIcons.SPEED_LIMIT_ZONE ||
      this.report.signType === MapSignsIcons.SPEED_LIMIT_ZONE_END
    )
  }

  getMarkerIconName(): string {
    if (this.report.signType.startsWith('SPEED_LIMIT')) {
      return this.report.displayType === MapSignsDisplayTypes.DIGITAL
        ? `${this.report.signType}_${this.report.speedLimit}_DIGITAL`
        : `${this.report.signType}_${this.report.speedLimit}`
    }
    return this.report.displayType === MapSignsDisplayTypes.DIGITAL
      ? `${this.report.signType}_DIGITAL`
      : this.report.signType
  }

  async submitReport() {
    this.report.signId = null
    this.report.reportType = 'missing-sign'
    this.report.speedLimit = this.report.signType.startsWith('SPEED_LIMIT')
      ? this.report.speedLimit
      : null
    this.report.location = this.lastClickLocation
      ? { lat: this.lastClickLocation.lat, lng: this.lastClickLocation.lng }
      : null
    this.report.mapLink = this.getCurrentUrl()

    this.reportSubmitting.set(true)
    this.reportError.set(false)
    try {
      const response = await this.customerReportingService.submitReport(this.report)
      this.ticketKey.set(response.ticketKey)
      this.ticketUrl.set(response.ticketUrl)
      this.reportSubmitting.set(false)
      this.reportSubmitted.set(true)
    } catch {
      this.reportSubmitting.set(false)
      this.reportError.set(true)
    }
  }

  private resetForm() {
    this.report = this.createInitialReport()
    this.reportSubmitted.set(false)
    this.reportSubmitting.set(false)
    this.reportError.set(false)
    this.ticketKey.set(null)
    this.ticketUrl.set(null)
  }

  private createInitialReport(): SignReport {
    const account = this.authService.getAccountInfo()
    const datasource =
      this.appState.selectDatasource() ?? this.authService.getUserClaims().userDatasources[0] ?? ''
    const now = new Date()
    const todayLocal = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16)

    return {
      reportType: 'missing-sign',
      title: getDefaultSignReportTitle({ reportType: 'missing-sign', signType: this.signTypes[0] }),
      reporter: account?.name ?? account?.username ?? '',
      datasource,
      signType: this.signTypes[0],
      speedLimit: 50,
      displayType: this.signDisplayTypes[0],
      supplementarySigns: [],
      category: '',
      description: '',
      signId: null,
      occurrenceDateTime: todayLocal,
      location: this.lastClickLocation
        ? { lat: this.lastClickLocation.lat, lng: this.lastClickLocation.lng }
        : null,
      bearing: 0,
    }
  }

  private emptyReport(): SignReport {
    return {
      reportType: 'missing-sign',
      title: '',
      reporter: '',
      datasource: '',
      mapLink: null,
      signType: '',
      speedLimit: null,
      displayType: '',
      supplementarySigns: [],
      category: '',
      description: '',
      signId: null,
      occurrenceDateTime: null,
      location: null,
      bearing: null,
    }
  }

  private getCurrentUrl(): string | null {
    if (typeof window === 'undefined') {
      return null
    }

    return window.location.href
  }

  private closePopup() {
    if (this.popup) {
      this.popup.remove()
      this.popup = null
    }
    this.isMobileFormOpen.set(false)
    this.cleanupMarker()
    this.unregisterMapRotationListener()
  }

  private cleanupMarker() {
    if (this.marker) {
      this.marker.remove()
      this.marker = null
    }
  }

  private async createMarker(
    lngLat: { lng: number; lat: number },
    signType: string,
    bearing: number = 0,
    supplementarySigns: string[] = [],
  ) {
    this.cleanupMarker()
    const spriteData = await this.spritePositionService.getSpritePosition(signType, 'medium')
    if (!spriteData) return

    const el = document.createElement('div')
    el.style.width = `${spriteData.width}px`
    el.style.height = `${spriteData.height}px`
    el.style.display = 'flex'
    el.style.alignItems = 'center'
    el.style.justifyContent = 'center'

    const icon = document.createElement('div')
    icon.style.width = '100%'
    icon.style.height = '100%'
    icon.style.backgroundImage = `url('${spriteData.spriteUrl}')`
    icon.style.backgroundPosition = `-${spriteData.x}px -${spriteData.y}px`
    icon.style.backgroundSize = `${spriteData.spriteWidth}px ${spriteData.spriteHeight}px`
    icon.style.backgroundRepeat = 'no-repeat'
    icon.style.display = 'block'
    icon.style.transformOrigin = 'center center'

    el.appendChild(icon)

    this.marker = new maplibregl.Marker({ element: el, anchor: 'center' })
      .setLngLat([lngLat.lng, lngLat.lat])
      .addTo(this.mapService.map)

    this.registerMapRotationListener(icon, bearing)
    this.updateMarkerRotation(icon, bearing)
  }

  private updateMarkerRotation(icon: HTMLElement, userBearing: number) {
    const mapBearing = this.mapService.map.getBearing ? this.mapService.map.getBearing() : 0
    icon.style.transform = `rotate(${userBearing - mapBearing}deg)`
  }

  private registerMapRotationListener(icon: HTMLElement, userBearing: number) {
    this.unregisterMapRotationListener()
    this.mapRotationListener = () => this.updateMarkerRotation(icon, userBearing)
    this.mapService.map.on('rotate', this.mapRotationListener)
    this.mapService.map.on('move', this.mapRotationListener)
  }

  private unregisterMapRotationListener() {
    if (this.mapRotationListener) {
      this.mapService.map.off('rotate', this.mapRotationListener)
      this.mapService.map.off('move', this.mapRotationListener)
      this.mapRotationListener = null
    }
  }

  ngOnDestroy() {
    this.resetForm()
    this.closePopup()
    this.mapEventsService.detachMapClick(this.boundMapClick)
    this.mapService.map.getCanvas().style.cursor = ''
  }
}
